import { useCallback, useEffect, useState } from "react";
import {
  Archive,
  ChevronLeft,
  Inbox,
  Mail,
  Paperclip,
  RefreshCw,
  Send,
  Settings,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { PageHeader } from "@/components/layout/PageHeader";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface EmailAddress {
  address: string;
  name?: string;
}

interface EmailMessage {
  id: string;
  subject: string | null;
  from: string | null;
  fromName: string | null;
  to: EmailAddress[];
  cc: EmailAddress[];
  receivedDateTime: string | null;
  sentDateTime: string | null;
  isRead: boolean;
  hasAttachments: boolean;
  bodyPreview: string | null;
  importance: string | null;
  flagged: boolean;
  conversationId: string | null;
}

interface EmailDetail extends EmailMessage {
  bodyHtml: string | null;
  bodyText: string | null;
}

interface MailFolder {
  id: string;
  displayName: string;
  totalItemCount: number;
  unreadItemCount: number;
}

interface AccountInfo {
  fromAddress: string;
  fromName: string;
  configured: boolean;
}

type ActiveFolder = "inbox" | "sentitems" | "drafts" | "archive";

function axiosStatus(err: unknown): number | null {
  if (typeof err === "object" && err !== null && "response" in err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    return typeof status === "number" ? status : null;
  }
  return null;
}

const FOLDER_CONFIG: { key: ActiveFolder; label: string; icon: typeof Inbox; graphName: string }[] = [
  { key: "inbox", label: "Inbox", icon: Inbox, graphName: "inbox" },
  { key: "sentitems", label: "Sent", icon: Send, graphName: "sentitems" },
  { key: "drafts", label: "Drafts", icon: Mail, graphName: "drafts" },
  { key: "archive", label: "Archive", icon: Archive, graphName: "archive" },
];

export function EmailPage() {
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [activeFolder, setActiveFolder] = useState<ActiveFolder>("inbox");
  const [selectedMessage, setSelectedMessage] = useState<EmailDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const loadAccount = useCallback(async () => {
    try {
      const resp = await api().get("/api/email/account");
      setAccount(resp.data);
    } catch {
      setAccount(null);
    }
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const resp = await api().get("/api/email/folders");
      setFolders(resp.data);
    } catch {
      /* ignore */
    }
  }, []);

  const [permissionError, setPermissionError] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);

  const loadMessages = useCallback(async (folder: ActiveFolder) => {
    setLoading(true);
    setError(null);
    setPermissionError(false);
    setNotConfigured(false);
    try {
      const graphFolder = FOLDER_CONFIG.find((f) => f.key === folder)?.graphName ?? "inbox";
      const resp = await api().get("/api/email/messages", { params: { folder: graphFolder, top: 50 } });
      setMessages(resp.data.messages ?? []);
    } catch (err: unknown) {
      const status = axiosStatus(err);
      if (status === 403) {
        setPermissionError(true);
        setMessages([]);
      } else if (status === 503) {
        setNotConfigured(true);
        setMessages([]);
      } else {
        const msg = err instanceof Error ? err.message : "Failed to load emails";
        setError(msg);
        setMessages([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const openMessage = useCallback(async (id: string) => {
    setLoadingDetail(true);
    try {
      const resp = await api().get(`/api/email/messages/${id}`);
      setSelectedMessage(resp.data);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, isRead: true } : m)));
    } catch {
      /* ignore */
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadAccount();
    loadFolders();
    loadMessages("inbox");
  }, [loadAccount, loadFolders, loadMessages]);

  const switchFolder = (folder: ActiveFolder) => {
    setActiveFolder(folder);
    setSelectedMessage(null);
    loadMessages(folder);
  };

  const refresh = () => {
    loadFolders();
    loadMessages(activeFolder);
  };

  const getFolderCount = (graphName: string) => {
    const f = folders.find((fo) => fo.displayName?.toLowerCase() === graphName.replace("sentitems", "sent items"));
    return f?.unreadItemCount ?? 0;
  };

  if (showSettings) {
    return (
      <>
        <PageHeader
          title="Email Settings"
          actions={
            <Button variant="outline" onClick={() => setShowSettings(false)}>
              <ChevronLeft className="h-4 w-4" /> Back to Email
            </Button>
          }
        />
        <EmailSettings account={account} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Email"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
              <Settings className="h-4 w-4" /> Settings
            </Button>
          </div>
        }
      />

      {account && (
        <div className="mb-4 flex items-center gap-2 text-xs text-zinc-500">
          <Mail className="h-3.5 w-3.5" />
          Connected as <span className="font-medium text-zinc-300">{account.fromAddress}</span>
          <span className="text-zinc-600">({account.fromName})</span>
        </div>
      )}

      {notConfigured && (
        <Card className="mb-4 border-zinc-500/30 bg-zinc-500/[0.06]">
          <CardContent className="flex items-start gap-3 py-4">
            <Settings className="h-5 w-5 text-zinc-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-zinc-300">Email not configured</p>
              <p className="text-xs text-zinc-400">
                Microsoft 365 email integration requires GraphMail settings (TenantId, ClientId, ClientSecret)
                to be configured in the API. Contact your administrator.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {permissionError && (
        <Card className="mb-4 border-amber-500/30 bg-amber-500/[0.06]">
          <CardContent className="flex items-start gap-3 py-4">
            <Settings className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-300">Mail.Read permission required</p>
              <p className="text-xs text-zinc-400">
                Your Entra app can send email but needs <code className="rounded bg-zinc-800 px-1 py-0.5 text-amber-200">Mail.Read</code> or{" "}
                <code className="rounded bg-zinc-800 px-1 py-0.5 text-amber-200">Mail.ReadWrite</code>{" "}
                (Application type) to read the mailbox. Add it in{" "}
                <strong>Azure Portal &rarr; App registrations &rarr; API permissions</strong> and grant admin consent.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-4" style={{ minHeight: "calc(100vh - 200px)" }}>
        {/* Folder sidebar */}
        <div className="w-48 shrink-0 space-y-1">
          {FOLDER_CONFIG.map((f) => {
            const Icon = f.icon;
            const unread = f.key === "inbox" ? getFolderCount("inbox") : 0;
            return (
              <button
                key={f.key}
                onClick={() => switchFolder(f.key)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  activeFolder === f.key
                    ? "bg-primary/10 text-primary"
                    : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200",
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">{f.label}</span>
                {unread > 0 && (
                  <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    {unread}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Message list + detail */}
        <div className="flex flex-1 gap-4 min-w-0">
          {/* Message list */}
          <Card className={cn("flex-1 overflow-hidden", selectedMessage && "max-w-sm")}>
            <CardContent className="p-0">
              {error ? (
                <div className="p-6 text-center text-sm text-red-400">{error}</div>
              ) : loading ? (
                <div className="p-6 text-center text-sm text-zinc-500">Loading...</div>
              ) : messages.length === 0 ? (
                <div className="p-6 text-center text-sm text-zinc-500">No messages</div>
              ) : (
                <ul className="divide-y divide-border">
                  {messages.map((m) => (
                    <li key={m.id}>
                      <button
                        className={cn(
                          "w-full text-left px-4 py-3 transition-colors hover:bg-zinc-800/50",
                          selectedMessage?.id === m.id && "bg-primary/5",
                          !m.isRead && "border-l-2 border-l-primary",
                        )}
                        onClick={() => openMessage(m.id)}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={cn("text-sm truncate flex-1", !m.isRead && "font-semibold text-zinc-100")}>
                            {activeFolder === "sentitems"
                              ? m.to?.[0]?.name || m.to?.[0]?.address || "—"
                              : m.fromName || m.from || "—"}
                          </span>
                          <span className="text-[10px] text-zinc-500 shrink-0 tabular-nums">
                            {formatEmailDate(m.receivedDateTime || m.sentDateTime)}
                          </span>
                        </div>
                        <div className={cn("text-xs truncate", !m.isRead ? "text-zinc-300" : "text-zinc-500")}>
                          {m.subject || "(no subject)"}
                        </div>
                        <div className="text-[11px] text-zinc-600 truncate mt-0.5">
                          {m.bodyPreview?.slice(0, 100)}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {m.hasAttachments && <Paperclip className="h-3 w-3 text-zinc-500" />}
                          {m.flagged && <Star className="h-3 w-3 text-amber-400 fill-amber-400" />}
                          {m.importance === "high" && (
                            <span className="text-[9px] font-semibold text-red-400 uppercase">Important</span>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Detail pane */}
          {selectedMessage && (
            <Card className="flex-[2] overflow-hidden">
              <CardContent className="p-0">
                {loadingDetail ? (
                  <div className="p-6 text-center text-sm text-zinc-500">Loading...</div>
                ) : (
                  <div className="flex flex-col h-full">
                    <div className="border-b border-border px-5 py-4 space-y-2">
                      <div className="flex items-start justify-between gap-4">
                        <h2 className="text-base font-semibold leading-tight">
                          {selectedMessage.subject || "(no subject)"}
                        </h2>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="shrink-0"
                          onClick={() => setSelectedMessage(null)}
                        >
                          Close
                        </Button>
                      </div>
                      <div className="text-xs text-zinc-400 space-y-0.5">
                        <div>
                          <span className="text-zinc-500">From:</span>{" "}
                          {selectedMessage.fromName || selectedMessage.from}
                          {selectedMessage.fromName && (
                            <span className="text-zinc-600 ml-1">&lt;{selectedMessage.from}&gt;</span>
                          )}
                        </div>
                        <div>
                          <span className="text-zinc-500">To:</span>{" "}
                          {selectedMessage.to?.map((r) => r.name || r.address).join(", ") || "—"}
                        </div>
                        {selectedMessage.cc?.length > 0 && (
                          <div>
                            <span className="text-zinc-500">CC:</span>{" "}
                            {selectedMessage.cc.map((r) => r.name || r.address).join(", ")}
                          </div>
                        )}
                        <div>
                          <span className="text-zinc-500">Date:</span>{" "}
                          {formatEmailDateFull(selectedMessage.receivedDateTime || selectedMessage.sentDateTime)}
                        </div>
                        {selectedMessage.hasAttachments && (
                          <div className="flex items-center gap-1 text-zinc-500">
                            <Paperclip className="h-3 w-3" /> Attachments
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 overflow-auto p-5">
                      {selectedMessage.bodyHtml ? (
                        <div
                          className="prose prose-invert prose-sm max-w-none [&_*]:text-inherit"
                          dangerouslySetInnerHTML={{ __html: selectedMessage.bodyHtml }}
                        />
                      ) : (
                        <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-sans">
                          {selectedMessage.bodyText || selectedMessage.bodyPreview || ""}
                        </pre>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function EmailSettings({ account }: { account: AccountInfo | null }) {
  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <CardContent className="space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Connected Account
          </h3>
          {account?.configured ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                <span className="text-sm font-medium text-emerald-300">Connected via Microsoft 365</span>
              </div>
              <div className="text-sm text-zinc-300">
                <strong>{account.fromName}</strong> — {account.fromAddress}
              </div>
              <p className="text-xs text-zinc-500">
                Emails are sent and read using your Entra app registration (client credentials).
                Sent emails are automatically saved to the account&apos;s Sent Items.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-4 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-400" />
                <span className="text-sm font-medium text-amber-300">Not configured</span>
              </div>
              <p className="text-xs text-zinc-400">
                Set up GraphMail in appsettings.json with your Entra app registration credentials.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Business Entity
          </h3>
          <div className="rounded-lg border border-border/60 p-4 space-y-2">
            <div className="text-sm font-medium">{account?.fromName || "—"}</div>
            <div className="text-xs text-zinc-500">{account?.fromAddress || "—"}</div>
            <p className="text-xs text-zinc-600 pt-1">
              Currently configured for a single LLC. Multi-entity support can be added in the future.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Integration Details
          </h3>
          <div className="text-xs text-zinc-400 space-y-2">
            <div className="grid grid-cols-[120px_1fr] gap-y-1.5">
              <span className="text-zinc-500">Provider</span>
              <span>Microsoft Graph API</span>
              <span className="text-zinc-500">Auth</span>
              <span>Entra ID (Client Credentials)</span>
              <span className="text-zinc-500">Permissions</span>
              <span>Mail.ReadWrite, Mail.Send</span>
              <span className="text-zinc-500">Sent Items</span>
              <span>Auto-saved to Outlook Sent</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function formatEmailDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatEmailDateFull(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
